import { createFileRoute, Link, useLocation } from '@tanstack/react-router'
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  DatabaseZap,
  Network,
  RadioTower,
  Workflow,
} from 'lucide-react'
import { motion } from 'motion/react'
import { LandingPageLayout } from '@/components/landing/LandingPageLayout'
import { SectionScroller } from '@/components/landing/SectionScroller'
import { GridSpotlight } from '@/components/ui/grid-spotlight'
import { getDefaultIndustryBySlug, getIndustryBySlug, type IndustryData } from '@/data/industries'
import { designSystem } from '@/lib/design-system'
import { getLocaleFromPath, withLocalePath } from '@/lib/i18n-utils'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/{-$locale}/industries/$industry')({
  head: ({ params }) => {
    const industry = getDefaultIndustryBySlug(params.industry)
    const title = industry
      ? `${industry.title} — Ponte Solutions`
      : 'Industry — Ponte Solutions'
    const description = industry?.summary ??
      'Industry-specific connected product programs by Ponte Solutions.'

    return {
      meta: [
        { title },
        { name: 'description', content: description },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
      ],
    }
  },
  component: IndustryDetailPage,
})

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09 } },
}

const itemVariants = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } },
}

function onSectionMouseMove(e: React.MouseEvent<HTMLElement>) {
  const rect = e.currentTarget.getBoundingClientRect()
  e.currentTarget.style.setProperty('--spot-x', `${e.clientX - rect.left}px`)
  e.currentTarget.style.setProperty('--spot-y', `${e.clientY - rect.top}px`)
}

function IndustryDetailPage() {
  const { industry: slug } = Route.useParams()
  const location = useLocation()
  const locale = getLocaleFromPath(location.pathname)
  const industry = getIndustryBySlug(locale, slug)

  if (!industry) {
    return <IndustryNotFound />
  }

  return (
    <LandingPageLayout>
      <div className="ponte-service-page">
        <SectionScroller labels={['Overview', 'Signals', 'Layer', 'Outcomes']}>
          <IndustryHeroSection industry={industry} />
          <IndustrySignalsSection industry={industry} />
          <IndustryLayerSection industry={industry} />
          <IndustryOutcomesSection industry={industry} />
        </SectionScroller>
      </div>
    </LandingPageLayout>
  )
}

function IndustryNotFound() {
  const location = useLocation()
  const locale = getLocaleFromPath(location.pathname)

  return (
    <LandingPageLayout>
      <section className="relative flex min-h-[calc(100svh-4rem)] items-center px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <h1 className={cn(designSystem.typography.heading.h1, 'text-4xl font-semibold md:text-5xl')}>
            Industry page not found.
          </h1>
          <p className="mt-5 text-lg leading-8 text-muted-foreground">
            The industry page you requested does not exist yet.
          </p>
          <Link
            to={withLocalePath('/industries', locale)}
            className="mt-8 inline-flex min-h-11 items-center rounded-md bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Industries
          </Link>
        </div>
      </section>
    </LandingPageLayout>
  )
}

function IndustryHeroSection({ industry }: { industry: IndustryData }) {
  const location = useLocation()
  const locale = getLocaleFromPath(location.pathname)

  return (
    <section
      className="ponte-service-section ponte-service-section--hero relative isolate flex min-h-[calc(100svh-4rem)] items-center overflow-hidden px-4 py-10 text-white sm:px-6 lg:px-8"
      style={{ '--spot-x': '50%', '--spot-y': '50%' } as React.CSSProperties}
      onMouseMove={onSectionMouseMove}
    >
      <GridSpotlight />
      <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-10 lg:grid-cols-[0.9fr_1.1fr]">
        <motion.div
          className="max-w-3xl py-10"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: 'easeOut' }}
        >
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <Link
              to={withLocalePath('/industries', locale)}
              className="inline-flex items-center gap-2 text-sm font-medium text-white/58 transition hover:text-accent"
            >
              <ArrowLeft className="h-4 w-4" />
              Industries
            </Link>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/8 px-3 py-1.5 text-xs font-medium uppercase tracking-[0.14em] text-white/75">
              {industry.eyebrow}
            </div>
          </div>
          <h1
            className={cn(
              designSystem.typography.heading.h1,
              'max-w-4xl text-balance text-[clamp(2.7rem,7vw,5.4rem)] font-semibold leading-[0.94] tracking-normal text-white',
            )}
          >
            {industry.hero.title}{' '}
            <span className="text-accent">{industry.hero.accent}</span>
          </h1>
          <p className="mt-7 max-w-2xl text-pretty text-lg leading-8 text-white/72 md:text-xl">
            {industry.hero.description}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to={withLocalePath('/contact', locale)}
              className="inline-flex min-h-11 items-center rounded-md bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
            >
              Discuss this industry
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
            <Link
              to={withLocalePath('/services/connect', locale)}
              className="inline-flex min-h-11 items-center rounded-md border border-white/20 px-5 py-3 text-sm font-semibold text-white transition hover:border-accent/70 hover:bg-white/8"
            >
              Start with ingestion
            </Link>
          </div>
        </motion.div>

        <IndustrySignalPanel industry={industry} />
      </div>
    </section>
  )
}

function IndustrySignalPanel({ industry }: { industry: IndustryData }) {
  return (
    <motion.div
      className="relative"
      variants={containerVariants}
      initial="hidden"
      animate="show"
    >
      <div className="relative overflow-hidden rounded-2xl border border-white/12 bg-black/18 p-4 shadow-2xl shadow-black/30 backdrop-blur-md">
        <div className="rounded-xl border border-white/10 bg-white/[0.055] p-5">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/45">
                Industry signal map
              </p>
              <h2 className="mt-2 text-2xl font-semibold text-white">{industry.shortTitle}</h2>
            </div>
            <RadioTower className="h-6 w-6 text-accent" />
          </div>

          {/* Signals as vertical list — more readable than cramped 3-col grid */}
          <div className="mb-6 divide-y divide-white/8">
            {industry.signals.map((signal, index) => (
              <motion.div
                key={signal.label}
                variants={itemVariants}
                className="flex gap-4 py-4 first:pt-0 last:pb-0"
              >
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-accent/35 bg-accent/10 font-mono text-[10px] text-accent">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-white">{signal.label}</h3>
                  <p className="mt-1 text-xs leading-5 text-white/56">{signal.detail}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            {industry.metrics.map((metric) => (
              <div key={metric.label} className="rounded-xl border border-white/10 bg-white/[0.04] p-4 text-center">
                <div className="text-2xl font-semibold text-white">{metric.value}</div>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.16em] text-white/42">
                  {metric.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  )
}

function IndustrySignalsSection({ industry }: { industry: IndustryData }) {
  return (
    <section
      className="ponte-service-section relative isolate flex min-h-[calc(100svh-4rem)] items-center overflow-hidden px-4 py-16 sm:px-6 lg:px-8"
      style={{ '--spot-x': '50%', '--spot-y': '50%' } as React.CSSProperties}
      onMouseMove={onSectionMouseMove}
    >
      <GridSpotlight baseOpacity={0.16} highlightOpacity={0.3} />
      <div className="relative z-10 mx-auto grid w-full max-w-7xl gap-16 lg:grid-cols-[0.75fr_1.25fr] lg:items-center">
        <div>
          <span className="mb-4 block text-xs font-semibold uppercase tracking-[0.18em] text-accent">
            Operating reality
          </span>
          <h2 className={cn(designSystem.typography.heading.h1, 'text-balance text-4xl font-semibold md:text-5xl')}>
            The product is physical. The service expectation is digital.
          </h2>
          <p className="mt-6 text-lg leading-8 text-muted-foreground">
            Ponte focuses on industries where distributed hardware, field conditions, and service
            commitments create a clear reason to connect device data with business systems.
          </p>
        </div>

        {/* Editorial divided list — more legible than equal-weight card grid */}
        <div className="divide-y divide-border/50">
          {industry.fit.map((item, index) => (
            <motion.div
              key={item}
              className="flex items-start gap-5 py-5 first:pt-0 last:pb-0"
              initial={{ opacity: 0, x: 16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.4, delay: index * 0.07 }}
            >
              <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-accent/30 bg-accent/8 text-accent">
                <CheckCircle2 className="h-3.5 w-3.5" />
              </span>
              <p className="text-base leading-7 text-foreground/80">{item}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

function IndustryLayerSection({ industry }: { industry: IndustryData }) {
  const icons = [Network, DatabaseZap, Workflow]

  return (
    <section className="ponte-service-section ponte-service-section--mesh relative isolate flex min-h-[calc(100svh-4rem)] items-center overflow-hidden px-4 py-16 text-white sm:px-6 lg:px-8">
      <div className="relative z-10 mx-auto w-full max-w-7xl">
        <div className="mb-12 max-w-3xl">
          <span className="mb-4 block text-xs font-semibold uppercase tracking-[0.18em] text-accent">
            Ponte layer
          </span>
          <h2 className={cn(designSystem.typography.heading.h1, 'text-balance text-4xl font-semibold text-white md:text-5xl')}>
            From installed product to connected service platform.
          </h2>
        </div>

        {/* Stepper layout — arrows communicate the sequential data flow */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-stretch">
          {industry.layers.map((layer, index) => {
            const Icon = icons[index] ?? icons[0]
            return (
              <div key={layer.title} className="flex flex-1 flex-col lg:flex-row lg:items-stretch">
                <motion.div
                  className="flex flex-1 flex-col rounded-xl border border-white/12 bg-white/[0.06] p-6 backdrop-blur"
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.45, delay: index * 0.1 }}
                >
                  <div className="mb-6 flex items-start justify-between">
                    <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-accent/35 bg-accent/10 text-accent">
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="font-mono text-3xl font-semibold text-white/10 leading-none">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold text-white">{layer.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-7 text-white/60">{layer.detail}</p>
                </motion.div>

                {/* Arrow connector between steps — hidden after last item */}
                {index < industry.layers.length - 1 && (
                  <div className="flex items-center justify-center px-3 py-3 lg:py-0">
                    <ArrowRight className="h-4 w-4 rotate-90 text-accent/35 lg:rotate-0" />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

function IndustryOutcomesSection({ industry }: { industry: IndustryData }) {
  const location = useLocation()
  const locale = getLocaleFromPath(location.pathname)

  return (
    <section
      className="ponte-service-section ponte-service-section--cta relative isolate flex min-h-[calc(100svh-4rem)] items-center overflow-hidden px-4 py-16 text-white sm:px-6 lg:px-8"
      style={{ '--spot-x': '50%', '--spot-y': '50%' } as React.CSSProperties}
      onMouseMove={onSectionMouseMove}
    >
      <GridSpotlight gridSize={56} baseOpacity={0.16} highlightOpacity={0.34} />
      <div className="relative z-10 mx-auto grid w-full max-w-7xl gap-14 lg:grid-cols-[0.85fr_1.15fr] lg:items-center">
        <div>
          <span className="mb-4 block text-xs font-semibold uppercase tracking-[0.18em] text-accent">
            Business impact
          </span>
          <h2 className={cn(designSystem.typography.heading.h1, 'text-balance text-4xl font-semibold text-white md:text-5xl')}>
            The result is not another dashboard. It is better service.
          </h2>
          <p className="mt-6 text-lg leading-8 text-white/66">
            The value appears when product signals change how service teams prioritize work,
            customers receive clearer status, and management can see recurring value from the installed base.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to={withLocalePath('/contact', locale)}
              className="inline-flex min-h-11 items-center rounded-md bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
            >
              Discuss {industry.shortTitle}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
            <Link
              to={withLocalePath('/industries', locale)}
              className="inline-flex min-h-11 items-center rounded-md border border-white/20 px-5 py-3 text-sm font-semibold text-white transition hover:border-accent/70 hover:bg-white/8"
            >
              All Industries
            </Link>
          </div>
        </div>

        {/* Large impact statements — border-top accent, no cramped cards */}
        <div className="grid gap-0 sm:grid-cols-2">
          {industry.outcomes.map((outcome, index) => {
            const isLastOdd = industry.outcomes.length % 2 !== 0 && index === industry.outcomes.length - 1
            return (
            <motion.div
              key={outcome}
              className={cn('border-t border-white/12 p-6 first:border-l-0 sm:[&:nth-child(odd)]:border-r sm:[&:nth-child(odd)]:border-r-white/12', isLastOdd && 'sm:col-span-2')}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.42, delay: index * 0.08 }}
            >
              <span className="mb-3 block font-mono text-xs text-accent/60">
                {String(index + 1).padStart(2, '0')}
              </span>
              <p className="text-lg font-semibold leading-7 text-white">{outcome}</p>
            </motion.div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
